import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  BrandIconSpinner,
  Button,
  CloseIcon,
  DocPreview,
  FileUpload,
  Modal,
} from 'components';
import {
  handleDeleteResume,
  handleGetAllResume,
  handlePostResume,
  handleUpdateHighlightResume,
} from 'redux/features/UserResumeSlice';
import { PostArrowBack, PostBox } from 'pages/home/Home.style';
import {
  ResumeType,
  UploadResumeContainer,
  UserResumeContainer,
} from './Profiles.style';

import { ReactComponent as EditOutlined } from 'assert/images/Profile/edit.svg';
import { ReactComponent as NoDataFound } from 'assert/images/Common/noDataFound.svg';
import { ReactComponent as Download } from 'assert/images/Common/download.svg';
import { ReactComponent as ShareIcon } from 'assert/images/Home/share.svg';
import { ReactComponent as Delete } from 'assert/images/More/delete.svg';
import { IconButton } from '@mui/material';
import CheckBox from 'components/CheckBox';
import useWindowDimensions from 'components/Dimention';
import { getFileName, getFileType, swipeModalWidth } from 'utils/helper';
import SwipeableEdgeDrawer from 'components/SwipeableDrawer';
import ShareModal from 'pages/home/ShareModal';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { Share as ShareCapacitor } from '@capacitor/share';
import { ShareBaseUrl } from 'environment';

export default function UserResume() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { width } = useWindowDimensions();
  const isNative = Capacitor.isNativePlatform();
  const { getProfile } = useSelector((state) => state?.ProfileSlice);

  const isSmallScreen = width <= swipeModalWidth;

  const [shareModalOpen, setShareModalOpen] = useState(null);
  const [attachment, setAttachment] = useState();
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resumeList, setResumeList] = useState([]);
  const [resumeOpen, setResumeOpen] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(null);
  const [highlightLoading, setHighlightLoading] = useState(false);
  const [highlightResumeId, setHighlightResumeId] = useState(null);

  const handleReset = () => {
    setLoading(false);
    setDeleteOpen(null);
    setModalOpen(false);
  };

  const handleGetResumes = () => {
    dispatch(handleGetAllResume())
      .then((data) => {
        setResumeList(data?.data ?? []);
        data?.data.forEach((element) => {
          if (element?.highLight) {
            setHighlightResumeId(element.id);
          }
        });
        handleReset();
      })
      .catch((err) => {
        console.log(err);
        handleReset();
      });
  };

  useEffect(() => {
    handleGetResumes();
  }, []);

  const handleSubmit = () => {
    setLoading(true);
    const formData = new FormData();
    formData.append('resume', attachment?.[0]);
    dispatch(handlePostResume(formData))
      .then(() => {
        setLoading(false);
        handleGetResumes();
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  };

  const handleHighlight = (resumeId) => {
    setHighlightLoading(true);
    const payload = {
      resumeId,
      status: highlightResumeId === resumeId ? 0 : 1,
    };
    dispatch(handleUpdateHighlightResume(payload))
      .then(() => {
        setHighlightLoading(false);
        setHighlightResumeId(highlightResumeId === resumeId ? false : resumeId);
      })
      .catch((err) => {
        setHighlightLoading(false);
        console.log(err);
      });
  };

  const handleDelete = () => {
    setLoading(true);
    dispatch(handleDeleteResume(deleteOpen))
      .then(() => {
        handleGetResumes();
      })
      .catch((err) => {
        setLoading(false);
        setDeleteOpen(null);
        console.log(err);
      });
  };

  const modalContent = () => {
    return (
      <UploadResumeContainer>
        {resumeList?.length >= 3 ? (
          <div className="resume-helper-text">
            You cannot upload more than 5 attachments.
          </div>
        ) : (
          <>
            <BrandIconSpinner loading={loading} />
            <FileUpload
              required
              label={'Attachment'}
              value={attachment}
              handleFileSRC={(data) => setAttachment(data)}
              isUploadDoc
              resetTrigger={modalOpen}
            />
            <div className="action-btn">
              <Button
                label="Close"
                type="outlined"
                onClick={() => setModalOpen(false)}
              />
              <Button
                label="Submit"
                disabled={!attachment}
                onClick={handleSubmit}
              />
            </div>
          </>
        )}
      </UploadResumeContainer>
    );
  };

  const docPreviewContent = () => {
    return (
      <UploadResumeContainer>
        {getFileType(resumeOpen) === 'pdf' ? (
          <DocPreview src={resumeOpen} readOnly isScroll isScrollDownload />
        ) : (
          <iframe
            title={getFileName(resumeOpen)}
            src={`https://view.officeapps.live.com/op/embed.aspx?src=${resumeOpen}`}
            height="650px"
            width="100%"
            style={{ border: 'none' }}
          />
        )}
      </UploadResumeContainer>
    );
  };

  return (
    <>
      <PostArrowBack className="post-back">
        <CloseIcon iconType={'back'} onClick={() => navigate(-1)} />
        <div>Back</div>
      </PostArrowBack>
      <PostBox>
        <UserResumeContainer>
          <div className="resume-title-container">
            <div className="resume-title">Resumes</div>
            <div
              className="profile-upload-resume"
              onClick={() => setModalOpen(true)}
            >
              <EditOutlined />
            </div>
          </div>
          {resumeList.length > 0 && (
            <div className="resume-helper-text">
              Mark a resume to be shown on your profile
            </div>
          )}
          {resumeList.length > 0 ? (
            <div className="resume-item-container">
              {resumeList.map((resumeItem, index) => {
                return (
                  <div key={index} className="resume-item">
                    <ResumeType resumetype={getFileType(resumeItem.resume)}>
                      {getFileType(resumeItem.resume)}
                    </ResumeType>
                    <div className="resume-content">
                      <div
                        className="resume-name"
                        title={getFileName(resumeItem.resume)}
                        onClick={() => setResumeOpen(resumeItem.resume)}
                      >
                        {getFileName(resumeItem.resume)}
                      </div>
                      <div className="resume-created">
                        Added on{' '}
                        {moment(resumeItem.createdAt).format('DD/MM/YYYY')}
                      </div>
                      <div className="resume-action-container">
                        {getFileType(resumeItem.resume) === 'pdf' && (
                          <IconButton
                            size="small"
                            component="a"
                            href={resumeItem.resume}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Download className="resume-download-icon" />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (isNative) {
                              ShareCapacitor.share({
                                title: "Resume",
                                text: `${getProfile.displayName} · ${getProfile.username} Resume\n`,
                                url: `${ShareBaseUrl}/r/${getProfile.username}/${resumeItem.id}`,
                                dialogTitle: 'Share with...',
                              });
                            } else {
                              setShareModalOpen(resumeItem.id);
                            }
                          }}
                        >
                          <ShareIcon className="resume-share-icon" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => setDeleteOpen(resumeItem.id)}
                        >
                          <Delete className="resume-delete-icon" />
                        </IconButton>
                        <div className="resume-checkbox-icon">
                          <CheckBox
                            onChange={() => handleHighlight(resumeItem?.id)}
                            value={highlightResumeId === resumeItem?.id}
                            size="small"
                            disabled={highlightLoading}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="no-data-found">
              <NoDataFound />
              <div>No resumes added yet</div>
            </div>
          )}
        </UserResumeContainer>
      </PostBox>
      {isSmallScreen ? (
        <SwipeableEdgeDrawer
          title={'Manage Resume'}
          openDrawer={modalOpen}
          handleCloseDrawer={() => setModalOpen(false)}
        >
          {modalContent()}
        </SwipeableEdgeDrawer>
      ) : (
        <Modal
          modelTitle="Manage Resume"
          content={modalContent()}
          openModal={modalOpen}
          modalchanges="uploadResumeModal"
          handleCancelModal={() => {
            setModalOpen(false);
          }}
        />
      )}
      {isSmallScreen ? (
        <SwipeableEdgeDrawer
          title={resumeOpen && getFileName(resumeOpen)}
          openDrawer={resumeOpen}
          handleCloseDrawer={() => setResumeOpen(null)}
        >
          {resumeOpen && docPreviewContent()}
        </SwipeableEdgeDrawer>
      ) : (
        <Modal
          modelTitle={resumeOpen && getFileName(resumeOpen)}
          content={resumeOpen && docPreviewContent()}
          openModal={resumeOpen}
          modalchanges="uploadResumeModal"
          handleCancelModal={() => {
            setResumeOpen(null);
          }}
        />
      )}
      <Modal
        removeHeader
        modalchanges="jobConfirm"
        handleCancelModal={() => setDeleteOpen(null)}
        openModal={deleteOpen !== null}
        content={
          <>
            <BrandIconSpinner loading={loading} />
            <div className="confirmModal">
              <div className="title">Are you sure you want to delete?</div>
              <div>
                <Button
                  label="Cancel"
                  variant="text"
                  onClick={() => setDeleteOpen(null)}
                />
                <Button label="Yes" color="error" onClick={handleDelete} />
              </div>
            </div>
          </>
        }
      />
      <Modal
        handleCancelModal={() => {
          setShareModalOpen(null);
        }}
        openModal={shareModalOpen}
        modalchanges={'share-modal'}
        modelTitle={t('HomePage.Share')}
        width={500}
        closable
        content={
          <ShareModal path={`/r/${getProfile.username}/${shareModalOpen}`} />
        }
      />
    </>
  );
}
